import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

// Define types for our component
interface JsonToTomlProps {}

// TOML format options
type TomlFormat = 'standard' | 'compact' | 'expanded';

const JsonToToml: React.FC<JsonToTomlProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the TOML output
  const [tomlOutput, setTomlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('config');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for TOML format
  const [format, setFormat] = useState<TomlFormat>('standard');
  // State to track if the conversion happened
  const [isTomlGenerated, setIsTomlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for TOML preview
  const [showPreview, setShowPreview] = useState<boolean>(true);

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsTomlGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsTomlGenerated(false);
    setTomlOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setTomlOutput('');
    setError('');
    setFileName('config');
    setFormat('standard');
    setIsTomlGenerated(false);
  };

  // Helper function to check if a value should be quoted in TOML
  const needsQuotes = (str: string): boolean => {
    // Check if string contains characters that would need quotes
    // This is a simplification; complete TOML spec has more rules
    return /[^\w.-]/.test(str) || str === '';
  };

  // Helper function to format string for TOML
  const formatTomlString = (str: string): string => {
    if (needsQuotes(str)) {
      // Escape quotes and special characters
      const escaped = str
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      
      return `"${escaped}"`;
    }
    return str;
  };

  // Helper function to format date for TOML
  const formatTomlDate = (date: Date): string => {
    return date.toISOString();
  };

  // Generate TOML representation with proper indentation
  const generateToml = (obj: any, path: string = '', indent: number = 0): string => {
    let result = '';
    
    const formatValue = (value: any): string => {
      if (value === null || value === undefined) {
        return 'null';
      } else if (typeof value === 'string') {
        return formatTomlString(value);
      } else if (typeof value === 'number') {
        return String(value);
      } else if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
      } else if (value instanceof Date) {
        return formatTomlDate(value);
      } else if (Array.isArray(value)) {
        if (value.length === 0) return '[]';
        
        if (format === 'compact') {
          // Simple array with only primitive values
          const allPrimitive = value.every(v => 
            v === null || 
            v === undefined || 
            typeof v === 'string' || 
            typeof v === 'number' || 
            typeof v === 'boolean' || 
            v instanceof Date
          );
          
          if (allPrimitive) {
            const items = value.map(v => formatValue(v)).join(', ');
            return `[${items}]`;
          }
        }
        
        // For arrays of objects or complex nested arrays
        const items = value.map(v => {
          if (typeof v === 'object' && v !== null && !(v instanceof Date)) {
            return '\n' + ' '.repeat(indent + 2) + '{\n' + 
              generateToml(v, '', indent + 4).split('\n').map(line => ' '.repeat(indent + 4) + line).join('\n') + 
              '\n' + ' '.repeat(indent + 2) + '}';
          }
          return formatValue(v);
        }).join(format === 'expanded' ? ',\n' + ' '.repeat(indent + 2) : ', ');
        
        return `[${items}]`;
      }
      
      return 'null'; // Fallback
    };

    // Process regular objects
    if (typeof obj === 'object' && obj !== null && !(obj instanceof Date) && !Array.isArray(obj)) {
      const keys = Object.keys(obj);
      
      for (const key of keys) {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;
        
        // Handle nested objects (not arrays)
        if (typeof value === 'object' && value !== null && !(value instanceof Date) && !Array.isArray(value)) {
          // Add a section header for the table
          result += `\n[${currentPath}]\n`;
          // Add the table contents
          result += generateToml(value, '', indent);
        } 
        // Handle arrays of objects - these need to be [[table.array]] in TOML
        else if (Array.isArray(value) && value.length > 0 && 
                typeof value[0] === 'object' && value[0] !== null && !(value[0] instanceof Date) && !Array.isArray(value[0])) {
          // For each object in the array, create a [[table.array]] entry
          for (const item of value) {
            result += `\n[[${currentPath}]]\n`;
            result += generateToml(item, '', indent);
          }
        } 
        // Handle simple values
        else {
          const formattedValue = formatValue(value);
          result += `${formatTomlString(key)} = ${formattedValue}\n`;
        }
      }
    }
    
    return result;
  };

  // Convert JSON to TOML
  const convertJsonToToml = () => {
    setError('');
    setIsLoading(true);
    setIsTomlGenerated(false);
    setTomlOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      const toml = generateToml(jsonData);
      setTomlOutput(toml);
      setIsTomlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating TOML: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy TOML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(tomlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download TOML file
  const downloadToml = () => {
    const blob = new Blob([tomlOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'config'}.toml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to TOML Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to TOML configuration format. Paste your JSON data below to generate a formatted TOML document.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="format" className="block text-sm font-medium text-gray-700 mb-1">
                    TOML Format Style
                  </label>
                  <select
                    id="format"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={format}
                    onChange={(e) => setFormat(e.target.value as TomlFormat)}
                  >
                    <option value="standard">Standard</option>
                    <option value="compact">Compact (shorter arrays)</option>
                    <option value="expanded">Expanded (more readable)</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPreview"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                  />
                  <label htmlFor="showPreview" className="ml-2 block text-sm text-gray-700">
                    Show Preview
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToToml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to TOML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              TOML Output
            </label>
            
            {isTomlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy TOML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadToml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isTomlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    TOML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isTomlGenerated && (
              <div className="flex-grow flex flex-col">
                {showPreview ? (
                  <div className="mb-3">
                    <div className="mb-2 flex justify-between items-center">
                      <h3 className="font-medium text-gray-800">Preview:</h3>
                      <button
                        className="text-xs text-gray-600 hover:text-gray-800"
                        onClick={() => setShowPreview(false)}
                      >
                        Hide Preview
                      </button>
                    </div>
                    <div className="border border-gray-200 rounded-md h-48 overflow-auto p-3 mb-3 font-mono text-sm">
                      <pre className="whitespace-pre text-gray-800">{tomlOutput}</pre>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={() => setShowPreview(true)}
                    >
                      Show Preview
                    </button>
                  </div>
                )}
                
                <div className="flex-grow flex flex-col">
                  <label htmlFor="tomlOutput" className="text-sm font-medium text-gray-700 mb-1">
                    TOML Code:
                  </label>
                  <textarea
                    id="tomlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-xs focus:ring-blue-500 focus:border-blue-500"
                    value={tomlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isTomlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">TOML Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated TOML can be used for configuration files in many programming languages and applications.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to TOML Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into TOML (Tom's Obvious, Minimal Language) format, which is a config file format designed to be easy to read and write with clear semantics.
              The converter preserves your data structure while adapting it to TOML's hierarchical format.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Automatic Conversion</strong> - Handles nested objects, arrays, and primitive values</li>
              <li><strong>Table Generation</strong> - Creates proper TOML tables and table arrays from nested JSON</li>
              <li><strong>Format Options</strong> - Choose between standard, compact or expanded output styles</li>
              <li><strong>Input Validation</strong> - Validates your JSON input before conversion</li>
              <li><strong>Live Preview</strong> - See how the TOML will look before downloading</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Choose the "Expanded" format option for better readability with complex nested structures</li>
              <li>The "Compact" format is useful for simpler configurations with many arrays</li>
              <li>TOML is great for configuration files, especially for applications written in languages like Rust, Go, Python, and Ruby</li>
              <li>TOML handles dates, times, and basic data types like strings, integers, floats, and booleans natively</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">TOML vs. JSON: Key Differences</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>TOML is designed specifically for configuration files, while JSON is a general-purpose data interchange format</li>
              <li>TOML uses a hierarchical structure with tables and dotted keys, making it more readable for nested configurations</li>
              <li>TOML supports comments, which JSON doesn't</li>
              <li>TOML has explicit types for dates and times, while JSON doesn't have specific datetime types</li>
              <li>TOML has a more human-friendly syntax for configuration files</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToToml;